import React, { useState, useEffect, useCallback } from "react";
import { Badge, Box, Divider, IconButton, Typography, Avatar, Menu, MenuItem, Popover } from "@mui/material";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import MiscellaneousServicesOutlinedIcon from "@mui/icons-material/MiscellaneousServicesOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import NotificationsNoneRoundedIcon from "@mui/icons-material/NotificationsNoneRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import { useVendorAuth } from "../VendorAuthContext";
import {
  VendorNotificationItem,
  vendorGetNotifications,
  vendorMarkNotificationsRead,
} from "../vendorApi";

/* ═══════════════════════════════════════════════════════════════════════════
   VENDOR LAYOUT — Sidebar + Topbar shell (Stripe-like SaaS feel)
   ═══════════════════════════════════════════════════════════════════════════ */

const T = {
  navy: "#0f172a",
  navyLight: "#1e293b",
  navySidebar: "#0c1322",
  gold: "#d4a373",
  cream: "#f8f5f0",
  white: "#ffffff",
  bg: "#f1f5f9",
  slate: "#64748b",
  slateLt: "#94a3b8",
  line: "#e2e8f0",
};

const SIDEBAR_W = 250;
const SIDEBAR_COLLAPSED_W = 72;

interface NavItem {
  label: string;
  icon: React.ReactNode;
  path: string;
}

const NAV_ITEMS: NavItem[] = [
  { label: "Dashboard",    icon: <DashboardOutlinedIcon />,                path: "/vendor/dashboard" },
  { label: "Events",       icon: <EventOutlinedIcon />,                    path: "/vendor/events" },
  { label: "Assignments",  icon: <AssignmentOutlinedIcon />,               path: "/vendor/assignments" },
  { label: "Pricing",      icon: <AttachMoneyOutlinedIcon />,              path: "/vendor/pricing" },
  { label: "Services",     icon: <MiscellaneousServicesOutlinedIcon />,    path: "/vendor/services" },
  { label: "Availability", icon: <CalendarTodayOutlinedIcon />,            path: "/vendor/availability" },
  { label: "Revenue",      icon: <BarChartOutlinedIcon />,                 path: "/vendor/revenue" },
];

const VendorLayout: React.FC = () => {
  const { vendor, logout } = useVendorAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  /* Notification state */
  const [notifAnchor, setNotifAnchor] = useState<null | HTMLElement>(null);
  const [notifications, setNotifications] = useState<VendorNotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchNotifications = useCallback(async () => {
    try {
      const data = await vendorGetNotifications();
      setNotifications(data.notifications);
      setUnreadCount(data.unread_count);
    } catch {
      /* silently fail */
    }
  }, []);

  /* Poll notifications every 30s */
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const handleMarkAllRead = async () => {
    await vendorMarkNotificationsRead();
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    setUnreadCount(0);
  };

  const sideW = collapsed ? SIDEBAR_COLLAPSED_W : SIDEBAR_W;

  const handleLogout = async () => {
    setAnchorEl(null);
    await logout();
    navigate("/vendor/login");
  };

  const initials = vendor?.name
    ? vendor.name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()
    : "V";

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: T.bg }}>
      {/* ── Sidebar ── */}
      <Box
        sx={{
          width: sideW,
          minHeight: "100vh",
          bgcolor: T.navySidebar,
          display: "flex",
          flexDirection: "column",
          transition: "width 0.25s cubic-bezier(0.22, 1, 0.36, 1)",
          overflow: "hidden",
          flexShrink: 0,
          position: "fixed",
          left: 0,
          top: 0,
          bottom: 0,
          zIndex: 100,
        }}
      >
        {/* Logo area */}
        <Box
          sx={{
            height: 64,
            display: "flex",
            alignItems: "center",
            px: collapsed ? 0 : 2.5,
            justifyContent: collapsed ? "center" : "flex-start",
            gap: 1.5,
            borderBottom: "1px solid rgba(255,255,255,0.06)",
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "10px",
              bgcolor: "rgba(212,163,115,0.15)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <StorefrontOutlinedIcon sx={{ fontSize: 18, color: T.gold }} />
          </Box>
          {!collapsed && (
            <Box>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: T.white,
                  lineHeight: 1.2,
                  whiteSpace: "nowrap",
                }}
              >
                Vendor Portal
              </Typography>
              <Typography
                sx={{ fontSize: 10, color: T.slateLt, whiteSpace: "nowrap" }}
              >
                Durham Convention Centre
              </Typography>
            </Box>
          )}
        </Box>

        {/* Navigation */}
        <Box sx={{ flex: 1, py: 2, px: collapsed ? 1 : 1.5, overflow: "auto" }}>
          {NAV_ITEMS.map((item) => {
            const isActive =
              location.pathname === item.path ||
              location.pathname.startsWith(item.path + "/");

            return (
              <Box
                key={item.path}
                onClick={() => navigate(item.path)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  px: collapsed ? 0 : 2,
                  py: 1.2,
                  mb: 0.4,
                  borderRadius: "10px",
                  cursor: "pointer",
                  justifyContent: collapsed ? "center" : "flex-start",
                  bgcolor: isActive
                    ? "rgba(212,163,115,0.12)"
                    : "transparent",
                  transition: "all 0.15s ease",
                  "&:hover": {
                    bgcolor: isActive
                      ? "rgba(212,163,115,0.15)"
                      : "rgba(255,255,255,0.04)",
                  },
                  "& .nav-icon": {
                    color: isActive ? T.gold : T.slateLt,
                    fontSize: 20,
                    transition: "color 0.15s",
                  },
                }}
              >
                <Box className="nav-icon" sx={{ display: "flex" }}>
                  {item.icon}
                </Box>
                {!collapsed && (
                  <Typography
                    sx={{
                      fontSize: 13,
                      fontWeight: isActive ? 600 : 500,
                      color: isActive ? T.white : T.slateLt,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {item.label}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>

        {/* Bottom collapse toggle */}
        <Box
          sx={{
            borderTop: "1px solid rgba(255,255,255,0.06)",
            px: collapsed ? 0 : 2,
            py: 1.5,
            display: "flex",
            justifyContent: collapsed ? "center" : "flex-end",
          }}
        >
          <IconButton
            onClick={() => setCollapsed(!collapsed)}
            sx={{
              width: 32,
              height: 32,
              color: T.slateLt,
              "&:hover": { color: T.white },
            }}
          >
            <MenuRoundedIcon
              sx={{
                fontSize: 18,
                transform: collapsed ? "rotate(0deg)" : "rotate(180deg)",
                transition: "transform 0.25s",
              }}
            />
          </IconButton>
        </Box>
      </Box>

      {/* ── Main content area ── */}
      <Box
        sx={{
          flex: 1,
          ml: `${sideW}px`,
          transition: "margin-left 0.25s cubic-bezier(0.22, 1, 0.36, 1)",
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
        }}
      >
        {/* ── Topbar ── */}
        <Box
          sx={{
            height: 64,
            bgcolor: T.white,
            borderBottom: `1px solid ${T.line}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            px: 3,
            flexShrink: 0,
          }}
        >
          {/* Notification Bell */}
          <IconButton
            onClick={(e) => setNotifAnchor(e.currentTarget)}
            sx={{
              mr: 1,
              width: 38,
              height: 38,
              borderRadius: "10px",
              transition: "all 0.15s",
              bgcolor: notifAnchor ? "#f1f5f9" : "transparent",
              "&:hover": { bgcolor: "#f1f5f9" },
            }}
          >
            <Badge
              badgeContent={unreadCount}
              max={9}
              sx={{
                "& .MuiBadge-badge": {
                  bgcolor: "#ef4444",
                  color: T.white,
                  fontSize: 9,
                  fontWeight: 700,
                  minWidth: 16,
                  height: 16,
                  padding: "0 4px",
                },
              }}
            >
              <NotificationsNoneRoundedIcon sx={{ fontSize: 20, color: T.slate }} />
            </Badge>
          </IconButton>

          {/* Notification Dropdown */}
          <Popover
            open={Boolean(notifAnchor)}
            anchorEl={notifAnchor}
            onClose={() => setNotifAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{
              sx: {
                mt: 1,
                width: 360,
                maxHeight: 440,
                borderRadius: "14px",
                boxShadow: "0 12px 40px rgba(0,0,0,0.12)",
                border: `1px solid ${T.line}`,
                overflow: "hidden",
              },
            }}
          >
            {/* Header */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2.5, py: 1.8, borderBottom: `1px solid ${T.line}` }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: T.navy }}>Notifications</Typography>
                {unreadCount > 0 && (
                  <Box sx={{ px: 1, py: 0.1, borderRadius: "6px", bgcolor: "#fef2f2" }}>
                    <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: "#ef4444" }}>{unreadCount} new</Typography>
                  </Box>
                )}
              </Box>
              {unreadCount > 0 && (
                <Box
                  onClick={handleMarkAllRead}
                  sx={{ display: "flex", alignItems: "center", gap: 0.4, cursor: "pointer", "&:hover .mark-text": { color: T.navy } }}
                >
                  <CheckCircleOutlineRoundedIcon sx={{ fontSize: 13, color: T.slateLt }} />
                  <Typography className="mark-text" sx={{ fontSize: 11, fontWeight: 500, color: T.slateLt, transition: "color 0.15s" }}>
                    Mark all read
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Notification list */}
            <Box sx={{ maxHeight: 360, overflow: "auto" }}>
              {notifications.length === 0 ? (
                <Box sx={{ py: 5, textAlign: "center" }}>
                  <NotificationsNoneRoundedIcon sx={{ fontSize: 32, color: T.slateLt, opacity: 0.4, mb: 1 }} />
                  <Typography sx={{ fontSize: 13, color: T.slateLt }}>No notifications</Typography>
                </Box>
              ) : (
                notifications.map((n) => (
                  <Box
                    key={n.id}
                    sx={{
                      px: 2.5, py: 1.8,
                      borderBottom: `1px solid ${T.line}`,
                      bgcolor: n.is_read ? "transparent" : "rgba(212,163,115,0.04)",
                      transition: "background 0.15s",
                      cursor: "pointer",
                      "&:hover": { bgcolor: "#f8fafc" },
                      display: "flex",
                      gap: 1.5,
                    }}
                    onClick={() => {
                      if (!n.is_read) {
                        vendorMarkNotificationsRead([n.id]);
                        setNotifications((prev) => prev.map((x) => x.id === n.id ? { ...x, is_read: true } : x));
                        setUnreadCount((c) => Math.max(0, c - 1));
                      }
                      if (n.meta?.event_id) {
                        setNotifAnchor(null);
                        navigate("/vendor/events");
                      }
                    }}
                  >
                    {/* Unread dot */}
                    <Box sx={{ pt: 0.5, width: 10, flexShrink: 0 }}>
                      {!n.is_read && <FiberManualRecordIcon sx={{ fontSize: 8, color: T.gold }} />}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 12.5, fontWeight: n.is_read ? 500 : 600, color: T.navy, lineHeight: 1.3 }}>
                        {n.title}
                      </Typography>
                      <Typography sx={{ fontSize: 11.5, color: T.slate, mt: 0.3, lineHeight: 1.4 }}>
                        {n.message}
                      </Typography>
                      <Typography sx={{ fontSize: 10, color: T.slateLt, mt: 0.5 }}>
                        {n.created_at}
                      </Typography>
                    </Box>
                  </Box>
                ))
              )}
            </Box>
          </Popover>

          {/* User Profile */}
          <Box
            onClick={(e) => setAnchorEl(e.currentTarget)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.2,
              cursor: "pointer",
              px: 1.5,
              py: 0.8,
              borderRadius: "10px",
              transition: "background 0.15s",
              "&:hover": { bgcolor: "#f8fafc" },
            }}
          >
            <Avatar
              sx={{
                width: 34,
                height: 34,
                bgcolor: T.gold,
                fontSize: 13,
                fontWeight: 700,
                color: T.white,
              }}
            >
              {initials}
            </Avatar>
            <Box>
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: T.navy,
                  lineHeight: 1.2,
                }}
              >
                {vendor?.name || "Vendor"}
              </Typography>
              <Typography
                sx={{ fontSize: 10.5, color: T.slate, lineHeight: 1.2 }}
              >
                {vendor?.company_name || vendor?.email}
              </Typography>
            </Box>
            <KeyboardArrowDownRoundedIcon
              sx={{ fontSize: 16, color: T.slateLt, ml: 0.5 }}
            />
          </Box>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            PaperProps={{
              sx: {
                mt: 1,
                borderRadius: "10px",
                minWidth: 180,
                boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
                border: `1px solid ${T.line}`,
              },
            }}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          >
            <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${T.line}` }}>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: T.navy }}>
                {vendor?.name}
              </Typography>
              <Typography sx={{ fontSize: 11, color: T.slate }}>
                {vendor?.email}
              </Typography>
              <Typography
                sx={{
                  fontSize: 10,
                  color: T.gold,
                  fontWeight: 600,
                  textTransform: "capitalize",
                  mt: 0.3,
                }}
              >
                {vendor?.role}
              </Typography>
            </Box>
            <MenuItem
              onClick={handleLogout}
              sx={{
                fontSize: 13,
                fontWeight: 500,
                color: "#ef4444",
                gap: 1.2,
                py: 1.2,
              }}
            >
              <LogoutRoundedIcon sx={{ fontSize: 16 }} />
              Sign out
            </MenuItem>
          </Menu>
        </Box>

        {/* Page content via Outlet */}
        <Box sx={{ flex: 1, p: 3, overflow: "auto" }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default VendorLayout;
